import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Note {
  id: string;
  user_id: string;
  brand_id: string;
  title: string;
  content: string;
  is_shared: boolean;
  created_at: string;
  updated_at: string;
  collaborators?: {
    user_id: string;
    role: string;
    full_name?: string;
    email?: string;
  }[];
}

export const useNotes = (brandId?: string) => {
  const { user } = useAuth();
  const [myNotes, setMyNotes] = useState<Note[]>([]);
  const [sharedNotes, setSharedNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchNotes = async () => {
    
    if (!user || !brandId) {
      // Clear notes when no user or brandId
      setMyNotes([]);
      setSharedNotes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch my notes (personal notes - not shared)
      const { data: myNotesData, error: myNotesError } = await supabase
        .from('notes')
        .select('*')
        .eq('brand_id', brandId)
        .eq('user_id', user.id)
        .eq('is_shared', false)  // Only non-shared personal notes
        .order('updated_at', { ascending: false });

      if (myNotesError) throw myNotesError;

      // Fetch all shared notes (including ones I created and ones shared with me)
      const { data: sharedNotesData, error: sharedNotesError } = await supabase
        .from('notes')
        .select('*')
        .eq('brand_id', brandId)
        .eq('is_shared', true)  // All shared notes, regardless of creator
        .order('updated_at', { ascending: false });

      if (sharedNotesError) throw sharedNotesError;

      // Fetch collaboration data for shared notes
      const notesWithCollaborators: Note[] = [];
      
      if (sharedNotesData && sharedNotesData.length > 0) {
        for (const note of sharedNotesData) {
          // Fetch collaborators for this note
          const { data: accessData, error: accessError } = await supabase
            .from('user_notes_access')
            .select(`
              user_id,
              role
            `)
            .eq('note_id', note.id);

          if (accessError) {
            notesWithCollaborators.push(note);
            continue;
          }

          // Fetch profile information for collaborators
          if (accessData && accessData.length > 0) {
            const userIds = accessData.map(access => access.user_id);
            const { data: profilesData, error: profilesError } = await supabase
              .from('profiles')
              .select('id, full_name, email')
              .in('id', userIds);

            if (profilesError) {
              // Error handled silently
            }

            // Combine access data with profile information
            const collaborators = accessData.map(access => {
              const profile = profilesData?.find(p => p.id === access.user_id);
              return {
                user_id: access.user_id,
                role: access.role,
                full_name: profile?.full_name,
                email: profile?.email,
              };
            });

            notesWithCollaborators.push({
              ...note,
              collaborators,
            });
          } else {
            notesWithCollaborators.push(note);
          }
        }
      }

      setMyNotes(myNotesData || []);
      setSharedNotes(notesWithCollaborators);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const createNote = async (title: string, content: string, sharedWithUserIds: string[] = []) => {
    if (!user || !brandId) return null;

    try {
      const { data, error } = await supabase
        .from('notes')
        .insert({
          user_id: user.id,
          brand_id: brandId,
          title,
          content,
          is_shared: sharedWithUserIds.length > 0,
        })
        .select()
        .single();

      if (error) throw error;

      // If sharing with specific users, create access records
      if (data && sharedWithUserIds.length > 0) {
        const accessRecords = sharedWithUserIds.map(userId => ({
          note_id: data.id,
          user_id: userId,
          brand_id: brandId,
          role: 'viewer' as const
        }));

        const { error: accessError } = await supabase
          .from('user_notes_access')
          .insert(accessRecords);

        if (accessError) {
          // Error handled silently
        }
      }

      // Refresh notes
      await fetchNotes();
      return data;
    } catch (error) {
      return null;
    }
  };

  const updateNote = async (noteId: string, updates: Partial<Pick<Note, 'title' | 'content' | 'is_shared'>>, sharedWithUserIds?: string[]) => {
    try {
      // Update sharing if provided - handle is_shared flag automatically
      let finalUpdates = { ...updates };
      if (sharedWithUserIds !== undefined) {
        finalUpdates.is_shared = sharedWithUserIds.length > 0;
      }

      const { error } = await supabase
        .from('notes')
        .update(finalUpdates)
        .eq('id', noteId);

      if (error) throw error;

      // Update sharing if provided
      if (sharedWithUserIds !== undefined) {
        // First, remove existing access records
        await supabase
          .from('user_notes_access')
          .delete()
          .eq('note_id', noteId);

        // Then add new ones if any
        if (sharedWithUserIds.length > 0) {
          const accessRecords = sharedWithUserIds.map(userId => ({
            note_id: noteId,
            user_id: userId,
            brand_id: brandId!,
            role: 'viewer' as const
          }));

          const { error: accessError } = await supabase
            .from('user_notes_access')
            .insert(accessRecords);

          if (accessError) {
            // Error handled silently
          }
        }
      }

      // Refresh notes
      await fetchNotes();
      return true;
    } catch (error) {
      return false;
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      // Refresh notes
      await fetchNotes();
      return true;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [user, brandId]);

  return {
    myNotes,
    sharedNotes,
    loading,
    createNote,
    updateNote,
    deleteNote,
    refetch: fetchNotes,
  };
};